import type { EventCommunicationDeliveryType, EventEditorInput } from "./eventEditor.js";

export type EventEmailDraft = { subject: string | null; message: string | null };
export type EventEmailTemplate = { subject: string; message: string };

export const eventEmailCopy = {
  en: {
    subject: "Subject",
    body: "Message",
    delivery: "Message type",
    preview: "Email preview",
    published: "Event invitation",
    update: "Event update",
    urgent: "Urgent notice",
    reset: "Reset to event details",
    testAddress: "Test recipient",
    send: "Send test email",
    sending: "Sending test...",
    invalid: "Enter a valid test email address.",
    required: "Enter an email subject and message before sending.",
    captured:
      "Test captured only. Email sending is not enabled for this environment; no email was sent.",
    accepted:
      "Test email accepted by the email provider. Check the recipient inbox and spam folder.",
    blocked: "Test not sent. This recipient is not approved for email testing in this environment.",
    configuration:
      "Test not sent. The email sender is not configured. Contact CloudPixel to enable it.",
    failed: "Test email was not sent. Check the email service configuration and try again.",
    empty: "No eligible recipients. No messages were sent.",
    dispatched: "Messages accepted by the delivery provider.",
    partial: "Some messages could not be sent. Review the delivery history.",
    duplicate: "This request was already processed. Review the delivery history.",
  },
  ar: {
    subject: "الموضوع",
    body: "الرسالة",
    delivery: "نوع الرسالة",
    preview: "معاينة البريد",
    published: "دعوة للفعالية",
    update: "تحديث الفعالية",
    urgent: "إشعار عاجل",
    reset: "إعادة ضبط الرسالة من تفاصيل الفعالية",
    testAddress: "مستلم الاختبار",
    send: "إرسال بريد تجريبي",
    sending: "جارٍ إرسال الاختبار...",
    invalid: "أدخل عنوان بريد إلكتروني صالحًا للاختبار.",
    required: "أدخل موضوع البريد ونص الرسالة قبل الإرسال.",
    captured: "تم تسجيل الاختبار فقط. الإرسال غير مفعّل في هذه البيئة؛ لم يُرسل أي بريد.",
    accepted: "قبل مزود البريد الرسالة التجريبية. تحقق من البريد الوارد والبريد غير المرغوب فيه.",
    blocked: "لم يُرسل الاختبار. هذا المستلم غير معتمد لاختبار البريد في هذه البيئة.",
    configuration: "لم يُرسل الاختبار. خدمة البريد غير مهيأة. تواصل مع CloudPixel لتفعيلها.",
    failed: "لم يُرسل البريد التجريبي. تحقق من إعدادات خدمة البريد وحاول مجددًا.",
    empty: "لا يوجد مستلمون مؤهلون. لم تُرسل رسائل.",
    dispatched: "قبل مزود الإرسال الرسائل.",
    partial: "تعذر إرسال بعض الرسائل. راجع سجل التسليم.",
    duplicate: "تمت معالجة هذا الطلب سابقًا. راجع سجل التسليم.",
  },
} as const;

export function resolveEventEmailDraft(
  generated: EventEmailTemplate,
  draft: EventEmailDraft,
): EventEmailTemplate {
  return {
    subject: draft.subject ?? generated.subject,
    message: draft.message ?? generated.message,
  };
}

export function buildEventEmailTemplate(input: {
  editor: EventEditorInput;
  organizationName: string;
  locale: "en" | "ar";
  deliveryType: EventCommunicationDeliveryType;
}): EventEmailTemplate {
  const { editor, organizationName, locale, deliveryType } = input;
  const ar = locale === "ar";
  const title = (ar ? editor.titleAr.trim() || editor.title : editor.title).trim();
  const summary = (ar ? editor.summaryAr.trim() || editor.summary : editor.summary).trim();
  const description = (
    ar ? editor.descriptionAr.trim() || editor.description : editor.description
  ).trim();
  // These fields already use the editor's local wall-clock convention. Format
  // both dates, including the year, so overnight events never lose their end date.
  const format = (value: string) => {
    const date = new Date(value);
    return Number.isFinite(date.getTime())
      ? date.toLocaleString(locale, {
          year: "numeric",
          month: "short",
          day: "numeric",
          weekday: "short",
          hour: "numeric",
          minute: "2-digit",
        })
      : "";
  };
  const dates = [format(editor.startsAt), format(editor.endsAt)].filter(Boolean).join(" - ");
  const location = [editor.campusNameSnapshot, editor.locationName, editor.addressLine1]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(" · ");
  const greeting = ar ? "السلام عليكم،" : "Assalamu alaikum,";
  const name = title || (ar ? "الفعالية" : "Event");
  const subject =
    deliveryType === "urgentNotice"
      ? `${ar ? "إشعار عاجل" : "Urgent notice"}: ${name}`
      : deliveryType === "materialUpdate"
        ? `${ar ? "تحديث" : "Update"}: ${name}`
        : `${ar ? "دعوة" : "You're invited"}: ${name}`;
  const intro =
    deliveryType === "eventPublished"
      ? ar
        ? `تدعوكم ${organizationName} إلى ${name}.`
        : `${organizationName} invites you to ${name}.`
      : ar
        ? `تشارككم ${organizationName} تحديثًا حول ${name}.`
        : `${organizationName} is sharing an update about ${name}.`;
  const details = [
    dates ? `${ar ? "الموعد" : "When"}: ${dates}` : "",
    location ? `${ar ? "المكان" : "Where"}: ${location}` : "",
    editor.onlineUrl.trim() ? `${ar ? "الرابط" : "Online"}: ${editor.onlineUrl.trim()}` : "",
    editor.registrationEnabled
      ? ar
        ? "التسجيل متاح في تطبيق شديد."
        : "Register in the Shdeed app."
      : ar
        ? "لا يلزم التسجيل."
        : "No registration is required.",
  ]
    .filter(Boolean)
    .join("\n");
  return {
    subject,
    message: [
      greeting,
      intro,
      details,
      summary,
      description !== summary ? description : "",
      ar ? "افتح تطبيق شديد للتفاصيل." : "Open the Shdeed app for details.",
    ]
      .filter(Boolean)
      .join("\n\n"),
  };
}

export function eventDeliveryFeedback(
  result: Record<string, unknown>,
  locale: "en" | "ar",
  test = true,
): { message: string; error: boolean } {
  const copy = eventEmailCopy[locale];
  const codes = Array.isArray(result.errorCodes) ? result.errorCodes : [];
  if (codes.includes("recipient_blocked_by_dev_safety"))
    return { message: copy.blocked, error: true };
  if (codes.includes("ses_not_configured") || codes.includes("CredentialsProviderError"))
    return { message: copy.configuration, error: true };
  if (result.status === "captured" || Number(result.capturedCount) > 0)
    return { message: copy.captured, error: true };
  if (result.status === "no_recipients") return { message: copy.empty, error: true };
  if (result.status === "partial_failure") return { message: copy.partial, error: true };
  if (result.status === "provider_accepted" && Number(result.providerAcceptedCount) > 0)
    return { message: test ? copy.accepted : copy.dispatched, error: false };
  if (result.outcome === "communication_already_processed")
    return { message: copy.duplicate, error: false };
  return { message: copy.failed, error: true };
}
